import { useTranslation } from 'next-i18next/pages'
import type { PricelistsProps } from './types'
import styles from './Pricelists.module.scss'
import parse from 'html-react-parser'
import PriceHistoryElement from 'components/charts/price-history/PriceHistoryElement'
import EncyclopediaArticlesReference from 'sections/encyclopedia-articles-reference/EncyclopediaArticlesReference'
import { Card, Container, Header, Heading, HeadingAndContent, List } from '@boltenergy-be/design-system'
import PricelistLine from 'features/pricelists/components/PricelistLine'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'

const Pricelists = ({ products, encyclopediaArticles, priceHistory, currentDate, minStartDate, endDate }: PricelistsProps) => {
  // i18n
  const { t } = useTranslation('pricelists')

  return (
    <main>
      {/* HERO */}
      <Header docked title={parse(t('hero.title'))} shape="s2" color="accent-warm">
        {t('hero.description')}
      </Header>

      {/* PRICELISTS */}
      {products && (
        <Container as="section" classnames={{ content: styles.pricelists }}>
          {/* ELECTRICITY */}
          <Card>
            <Heading as="h2" serif>
              {t('electricity.title')}
            </Heading>

            <List as="ul">
              {products.electricity?.bolt.map((pricelist) => (
                <PricelistLine key={`bolt-el-${pricelist.product}`} {...pricelist} />
              ))}
            </List>
            {!!products.electricity?.plenty?.length && (
              <List as="ul">
                {products.electricity?.plenty.map((pricelist) => (
                  <PricelistLine key={`plenty-el-${pricelist.product}`} {...pricelist} />
                ))}
              </List>
            )}
          </Card>

          <Card>
            <Heading as="h2" serif>
              {t('gas.title')}
            </Heading>
            <List as="ul">
              {products.gas?.bolt.map((pricelist) => (
                <PricelistLine key={`bolt-ng-${pricelist.product}`} {...pricelist} />
              ))}
            </List>

            {!!products.gas?.plenty?.length && (
              <List as="ul">
                {products.gas?.plenty.map((pricelist) => (
                  <PricelistLine key={`plenty-ng-${pricelist.product}`} {...pricelist} />
                ))}
              </List>
            )}
          </Card>
        </Container>
      )}

      {/* CALL TO ACTION */}
      <HeadingAndContent title={t('cta.title')} inset color="dark" shape="s4">
        <p>{t('cta.description')}</p>
        <StartSimulationButton />
      </HeadingAndContent>

      {/* GRAPH */}
      <Container as="section" classnames={{ content: styles.graphs }}>
        <header>
          <Heading as="h2" variant="h3">
            {t('chart.title')}
          </Heading>
          <p>{t('chart.description')}</p>
        </header>

        <PriceHistoryElement
          priceHistoryData={priceHistory}
          currentDateString={currentDate}
          minStartDateString={minStartDate}
          endDateString={endDate}
        />
      </Container>

      {!!encyclopediaArticles?.length && <EncyclopediaArticlesReference title={t('encyclopedia.title')} articles={encyclopediaArticles} />}
    </main>
  )
}

export default Pricelists
